import { FC } from "react";
import classNames from "classnames";

import { KpiCardsContentBlockData } from "../ContentBuilder.type";

interface KpiBlockProps {
	data: KpiCardsContentBlockData;
}

const KpiBlock: FC<KpiBlockProps> = ({ data }) => {
	const { items } = data;

	return (
		// One joined strip rather than separate cards: it holds together as a single block at the
		// head of a section, which suits the report better than three floating cards.
		<div className="flex flex-col sm:flex-row">
			{items.map((item, index) => {
				const isFirst = index === 0;
				const isLast = index === items.length - 1;

				// Capitalised so JSX treats it as a component rather than an `<icon>` element.
				const Icon = item.icon;

				const comparisonValue = item.comparison ? parseFloat(item.comparison) : null;
				const isPositive = comparisonValue !== null && comparisonValue > 0;
				const isNegative = comparisonValue !== null && comparisonValue < 0;

				return (
					<div
						key={index}
						className={classNames(
							// `overflow-hidden` is what lets the accent rule below follow the rounded
							// corners of the cards at either end of the strip.
							"flex-1 bg-white border border-gray-400 flex flex-col overflow-hidden",
							isFirst && isLast
								? "rounded-lg"
								: {
									"rounded-tl-lg rounded-tr-lg sm:rounded-tr-none sm:rounded-bl-lg": isFirst,
									"rounded-bl-lg rounded-br-lg sm:rounded-bl-none sm:rounded-tr-lg": isLast,
									"border-t-0 sm:border-t sm:border-l-0": !isFirst,
								},
						)}
					>
						{/* The light brand blue only reaches ~2.4:1 on white, so it cannot carry text.
						    Spent here instead, where contrast does not apply: the cards sit flush
						    against each other, so across a row the rules join into a single line. */}
						<div className="h-[3px] bg-primary shrink-0" aria-hidden="true" />

						<div className="px-5 py-4 flex flex-col gap-1">
							{/* The icon shares the figure's row rather than taking one of its own: on a
							    strip this compact, an extra row costs more height than the marker is
							    worth. `items-start` still lands it in the card's top corner. */}
							<div className="flex items-start justify-between gap-3">
								<div className="flex items-baseline gap-1.5">
									{/* `secondary` rather than `primary`, for the contrast reason above. */}
									<span className="text-3xl font-black text-secondary">{item.value}</span>
									{/* A step up from the description below it: the unit belongs to the
									    figure, not to the caption, so it should not sit at caption weight. */}
									<span className="text-base text-gray-700">{item.unit}</span>
								</div>

								{/* Decorative: the description already says what the icon illustrates.
								    Held at 60% so it marks the card without rivalling the figure, which
								    carries the same colour at full strength. */}
								{Icon ? (
									<Icon
										className="w-4 h-4 shrink-0 mt-1 text-secondary/60"
										aria-hidden="true"
										focusable="false"
									/>
								) : null}
							</div>

							{item.comparison && (
								<span
									className={classNames("text-sm font-medium", {
										"text-green-600": isPositive,
										"text-red-500": isNegative,
										"text-gray-500": !isPositive && !isNegative,
									})}
								>
									{item.comparison}
								</span>
							)}

							<p className="text-sm text-gray-600 mt-1">{item.description}</p>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export { KpiBlock };
